<script setup lang="ts">
import { api } from '@/api';
import { DataSetDto } from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { useUnitsStore } from '@/stores/unitsStore.ts';
import ActiveFilters from '@/views/TrendsSelectionView/components/ActiveFilters.vue';
import DataSetFiltering from '@/views/TrendsSelectionView/components/DataSetFiltering.vue';
import DataSetSelection from '@/views/TrendsSelectionView/components/DataSetSelection.vue';
import { useDataSetFiltering } from '@/views/TrendsSelectionView/useDataSetFiltering.ts';
import { onMounted, ref, unref } from 'vue';
import { useRouter } from 'vue-router';
import debounce from 'lodash/debounce';

const unitsStore = useUnitsStore();
const router = useRouter();

const {
  startDate,
  endDate,
  searchFilter,
  maximumDataSetCount,
  shouldFetchData,
  initializeFilters,
} = useDataSetFiltering();

const totalDataSetCount = ref<number>(0);
const dataSets = ref<DataSetDto[] | null>(null);
const selectedDataSets = ref<string[]>([]);
const showAllCells = ref(false);
const loadingFilter = ref(false);

function confirmFilterChanged() {
  if (!shouldFetchData.value) {
    dataSets.value = null;
    totalDataSetCount.value = 0;
    return;
  }
  selectedDataSets.value = [];
  filterForParameterCombinationsDebounced();
}

onMounted(async () => {
  await initializeFilters();
  await unitsStore.initialize();
  if (shouldFetchData.value) {
    await filterForParameterCombinations();
  }
});

const filterForParameterCombinationsDebounced = debounce(() => {
  filterForParameterCombinations();
}, 600);

async function filterForParameterCombinations() {
  loadingFilter.value = true;
  // Only initially set dataSets to null if the browser is Firefox to prevent the freezing bug in ff
  const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);
  if (isFirefox) {
    dataSets.value = null;
  }

  if (searchFilter.value.startDate && searchFilter.value.endDate) {
    const { result } = await api.dataSetApi.getDataSets({
      dataSetRequestDto: searchFilter.value,
    });
    totalDataSetCount.value = result?.totalDataSetCount ?? 0;
    if (result?.dataSets) {
      dataSets.value = result.dataSets;
    } else {
      dataSets.value = null;
    }
  } else {
    dataSets.value = null;
  }

  loadingFilter.value = false;
}

async function select() {
  await router.push({
    name: routenames.trendDisplay,
    query: {
      start: startDate.value?.toISO(),
      end: endDate.value?.toISO(),
      dataSets: selectedDataSets.value,
    },
  });
}
</script>

<template>
  <BasePage>
    <template #title>
      <BasePageTitle :title="$t('Trends')" />
    </template>
    <template #info>
      <VSwitch v-model="showAllCells" color="primary" :label="$t('ShowAllCells')" hideDetails />
    </template>
    <template #actions>
      <div class="mr-4 font-weight-medium">
        {{ totalDataSetCount ?? 0 }}
        {{ $t('DatasetsFound') }} |
        {{
          $t('_count_DataSetsSelected', {
            count: selectedDataSets.length,
          })
        }}
      </div>

      <Button
        :disabled="selectedDataSets.length === 0"
        :label="$t('ViewDataSetDiagram')"
        variant="primary"
        @click="select"
      />
    </template>

    <DataSetFiltering
      :searchFieldPlaceholderText="$t('FilterSearchFieldPlaceholder')"
      @filtersChanged="confirmFilterChanged"
    />
    <ActiveFilters @filtersChanged="confirmFilterChanged" />

    <v-progress-linear v-show="loadingFilter" color="yellow-darken-2" indeterminate height="2" />

    <DataSetSelection
      v-model:selectedDataSets="selectedDataSets"
      :totalDataSetCount="totalDataSetCount"
      :maximumDataSetCount="unref(maximumDataSetCount)"
      :dataSets="dataSets"
      :loading="loadingFilter"
      :showDifferences="!showAllCells"
    />
  </BasePage>
</template>
